/**
 * scheduleAlgorithm.js
 * Pure functions for custom schedule generation — exported for testability.
 *
 * Design: hosts configure EXPLICIT matchup pairs (who plays who, how many games).
 * The algorithm then distributes those game instances across selected weeks and courts.
 */

// ── Week helpers ─────────────────────────────────────────────────────────────

const _DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Generate the list of available weeks from league dates and game day.
 * @param {Object} league - { season_start_date, season_end_date, game_day }
 * @returns {Array<{ number, date, formatted }>}
 */
export function generateAvailableWeeks(league) {
  if (!league?.season_start_date || !league?.season_end_date || !league?.game_day) {
    return [];
  }

  const startDate = new Date(league.season_start_date);
  const endDate   = new Date(league.season_end_date);

  const dayIndex  = _DAY_NAMES.indexOf(league.game_day);
  if (dayIndex === -1) return [];

  const weeks = [];
  const current = new Date(startDate);

  // Advance to first occurrence of game day
  while (current.getDay() !== dayIndex && current <= endDate) {
    current.setDate(current.getDate() + 1);
  }

  let weekNumber = 1;
  while (current <= endDate) {
    weeks.push({
      number: weekNumber,
      date: new Date(current),
      formatted: current.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    });
    current.setDate(current.getDate() + 7);
    weekNumber++;
  }

  return weeks;
}

/**
 * Detect whether a string looks like an ISO calendar date (YYYY-MM-DD).
 */
function _isIsoDate(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s));
}

/**
 * Return the ISO week key ("YYYY-Www") for a date — used to group recurring-
 * weekday slots that fall in the same calendar week.
 */
function _isoWeekKey(d) {
  const tmp = new Date(d);
  tmp.setHours(0, 0, 0, 0);
  tmp.setDate(tmp.getDate() + 4 - (tmp.getDay() || 7)); // Thu of the ISO week
  const yearStart = new Date(tmp.getFullYear(), 0, 1);
  const weekNo    = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  return `${tmp.getFullYear()}-W${weekNo}`;
}

/**
 * Generate a flat list of date slots for use in the Custom Schedule Creator.
 *
 * Supports two modes detected automatically from `league.game_days`:
 *
 *   • Specific-dates mode  — all entries in game_days look like "YYYY-MM-DD".
 *     Returns one slot per date, in chronological order, filtered to the season
 *     range.
 *
 *   • Recurring-weekdays mode — entries are day names ("Monday", "Thursday"…).
 *     Expands every occurrence of each selected day within the season range and
 *     sorts chronologically.  Slots that fall in the same ISO calendar week
 *     share the same `weekNumber` so the UI can group them.
 *
 * Falls back to the legacy `game_day` (single string) if `game_days` is absent.
 *
 * The returned objects are compatible with `generateMatchupSchedule`'s
 * `availableWeeks` parameter (same `number`, `date`, `formatted` shape, plus
 * extra display fields).
 *
 * @param {Object} league
 * @param {string}   league.season_start_date  - "YYYY-MM-DD"
 * @param {string}   league.season_end_date    - "YYYY-MM-DD"
 * @param {string[]|null} league.game_days     - ["Monday","Thursday"] OR ["2026-01-06","2026-01-09"]
 * @param {string|null}   league.game_day      - legacy single day fallback
 * @returns {Array<{ number, date, formatted, weekNumber, dayLabel, slotKey }>}
 */
export function generateAvailableSlots(league) {
  const days =
    Array.isArray(league?.game_days) && league.game_days.length > 0
      ? league.game_days
      : league?.game_day ? [league.game_day] : [];

  if (!days.length || !league?.season_start_date || !league?.season_end_date) return [];

  // Normalise to plain YYYY-MM-DD regardless of whether the field came back
  // as a full ISO timestamp (e.g. "2026-08-02T00:00:00.000Z") or a date string.
  const toDateStr = (v) => String(v).slice(0, 10);
  const start = new Date(toDateStr(league.season_start_date) + 'T00:00:00');
  const end   = new Date(toDateStr(league.season_end_date)   + 'T23:59:59');

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];

  // ── Specific-dates mode ──────────────────────────────────────────────────
  if (days.every(_isIsoDate)) {
    return days
      .map(d => new Date(d + 'T00:00:00'))
      .filter(d => d >= start && d <= end)
      .sort((a, b) => a - b)
      .map((date, i) => ({
        number:     i + 1,
        date,
        formatted:  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        weekNumber: i + 1,
        dayLabel:   date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        slotKey:    `date_${date.toISOString().split('T')[0]}`
      }));
  }

  // ── Recurring-weekdays mode ──────────────────────────────────────────────
  const allSlots = [];
  for (const dayName of days) {
    const dayIndex = _DAY_NAMES.indexOf(dayName);
    if (dayIndex === -1) continue;
    const cur = new Date(start);
    // Guard: advance to the first occurrence of dayName, but never past end
    while (cur.getDay() !== dayIndex && cur <= end) { cur.setDate(cur.getDate() + 1); }
    while (cur <= end) {
      allSlots.push({ date: new Date(cur), dayName });
      cur.setDate(cur.getDate() + 7);
    }
  }

  allSlots.sort((a, b) => a.date - b.date);

  // Assign weekNumber: slots sharing an ISO calendar week get the same value
  const weekKeyMap = new Map();
  let weekCounter  = 1;
  for (const { date } of allSlots) {
    const key = _isoWeekKey(date);
    if (!weekKeyMap.has(key)) weekKeyMap.set(key, weekCounter++);
  }

  return allSlots.map(({ date, dayName }, i) => {
    const weekNumber = weekKeyMap.get(_isoWeekKey(date));
    return {
      number:     i + 1,
      date,
      formatted:  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      weekNumber,
      dayLabel:   dayName,
      slotKey:    `w${weekNumber}_${dayName.slice(0, 3)}_${date.toISOString().split('T')[0]}`
    };
  });
}

/**
 * Given a season range and a weekday name, return all ISO date strings for
 * every occurrence of that weekday within the range.
 * Exposed for use in the schedule-date picker inside league forms.
 */
export function getDatesForWeekday(startDate, endDate, dayName) {
  if (!startDate || !endDate || !dayName) return [];
  const dayIndex = _DAY_NAMES.indexOf(dayName);
  if (dayIndex === -1) return [];
  const cur    = new Date(startDate + 'T00:00:00');
  const endD   = new Date(endDate   + 'T23:59:59');
  const result = [];
  while (cur.getDay() !== dayIndex && cur <= endD) cur.setDate(cur.getDate() + 1);
  while (cur <= endD) {
    result.push(cur.toISOString().split('T')[0]);
    cur.setDate(cur.getDate() + 7);
  }
  return result;
}

// ── Utility helpers ───────────────────────────────────────────────────────────

/**
 * Shuffle an array (Fisher-Yates). Returns a new array; original unchanged.
 * @param {Array} arr
 * @param {Function} [rng] - random number generator, defaults to Math.random
 */
export function shuffleArray(arr, rng = Math.random) {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Parse "HH:MM" or "HH:MM:SS" time string to minutes from midnight.
 */
export function parseTimeToMinutes(timeStr) {
  if (!timeStr) return 18 * 60; // default 6 pm
  const [h = 0, m = 0] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Format minutes from midnight as "HH:MM".
 */
export function minutesToTime(minutes) {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ── Schedule generation ───────────────────────────────────────────────────────

/**
 * Generate a schedule from explicit matchup pairs distributed across selected weeks.
 *
 * Algorithm:
 *   1. Expand each matchup into N game instances (N = matchup.games).
 *   2. Shuffle the instances for variety.
 *   3. For each selected week (chronological order):
 *        - Run "rounds" within the week (each round = one time slot).
 *        - In a round, fill each available court with a game where neither
 *          team is already playing in that time slot AND the team hasn't
 *          exceeded gamesPerTeamPerWeek for this week.
 *        - Stop adding rounds when no game can be assigned.
 *   4. Any unassigned instances overflow to subsequent weeks; unscheduled
 *      ones are dropped (caller should warn the user).
 *
 * @param {Object}   params
 * @param {Array}    params.matchups            - [{ team_1_id, team_2_id, team_1_name, team_2_name, games }]
 * @param {number[]} params.selectedWeeks       - Week numbers chosen by the host
 * @param {Object[]} params.availableWeeks      - [{ number, date, formatted }]
 * @param {Object}   params.league              - { number_of_courts, start_time }
 * @param {number}   [params.gamesPerTeamPerWeek=1] - Max games one team can play per week
 * @returns {Object[]} Scheduled game objects ready for the API
 */
export function generateMatchupSchedule({
  matchups,
  selectedWeeks,
  availableWeeks,
  league,
  gamesPerTeamPerWeek = 1
}) {
  if (!matchups?.length || !selectedWeeks?.length) return [];

  // 1. Expand matchups → individual game instances
  const gameInstances = [];
  for (const matchup of matchups) {
    const count = Math.max(1, Math.min(10, parseInt(matchup.games, 10) || 1));
    for (let g = 0; g < count; g++) {
      gameInstances.push({
        team_1_id:   matchup.team_1_id,
        team_2_id:   matchup.team_2_id,
        team_1_name: matchup.team_1_name,
        team_2_name: matchup.team_2_name
      });
    }
  }

  // 2. Shuffle for variety
  const unassigned = shuffleArray(gameInstances);

  // Pre-compute constants
  const weekDataMap        = new Map(availableWeeks.map(w => [w.number, w]));
  const courts             = Math.max(1, parseInt(league.number_of_courts, 10) || 1);
  const startMinutes       = parseTimeToMinutes(league.start_time);
  const gameDurationMins   = 60; // 1 hour per game slot
  const maxGamesPerWeek    = Math.max(1, parseInt(gamesPerTeamPerWeek, 10) || 1);

  const schedule = [];
  let nextId = 1;

  // 3. Assign to weeks
  const sortedWeeks = [...selectedWeeks].sort((a, b) => a - b);

  for (const weekNum of sortedWeeks) {
    if (unassigned.length === 0) break;

    const weekData = weekDataMap.get(weekNum);
    if (!weekData) continue;

    const teamGamesThisWeek = new Map(); // teamId → games count this week
    let round = 0;

    // Keep adding rounds until no game can be placed in a round
    let roundHadGames = true;
    while (roundHadGames && unassigned.length > 0) {
      round++;
      const slotMinutes  = startMinutes + (round - 1) * gameDurationMins;
      const gameTime     = minutesToTime(slotMinutes);
      const busyThisSlot = new Set(); // teams already placed in this time slot
      let assignedInSlot = 0;

      for (let courtIdx = 0; courtIdx < courts; courtIdx++) {
        const gameIdx = unassigned.findIndex(g =>
          !busyThisSlot.has(g.team_1_id) &&
          !busyThisSlot.has(g.team_2_id) &&
          (teamGamesThisWeek.get(g.team_1_id) || 0) < maxGamesPerWeek &&
          (teamGamesThisWeek.get(g.team_2_id) || 0) < maxGamesPerWeek
        );
        if (gameIdx === -1) continue;

        const game = unassigned.splice(gameIdx, 1)[0];

        schedule.push({
          id:            nextId++,
          week_number:   weekNum,
          game_date:     weekData.date.toISOString().split('T')[0],
          game_time:     gameTime,
          team_1_id:     game.team_1_id,
          team_2_id:     game.team_2_id,
          team_1_name:   game.team_1_name,
          team_2_name:   game.team_2_name,
          // actual_id aliases ensure both /custom-schedule and /bulk-matches receive teams.id
          team_1_actual_id: game.team_1_id,
          team_2_actual_id: game.team_2_id,
          court_number:  courtIdx + 1,
          status:        'scheduled',
          round
        });

        busyThisSlot.add(game.team_1_id);
        busyThisSlot.add(game.team_2_id);
        teamGamesThisWeek.set(game.team_1_id, (teamGamesThisWeek.get(game.team_1_id) || 0) + 1);
        teamGamesThisWeek.set(game.team_2_id, (teamGamesThisWeek.get(game.team_2_id) || 0) + 1);
        assignedInSlot++;
      }

      roundHadGames = assignedInSlot > 0;
    }
  }

  return schedule;
}

/**
 * Build the set of all unique head-to-head pair keys from a list of team IDs.
 * Key format: "minId-maxId" for order-independent deduplication.
 * @param {number[]} teamIds
 * @returns {Array<[number, number]>} All unique pairs as [id1, id2] tuples
 */
export function getAllPairs(teamIds) {
  const pairs = [];
  for (let i = 0; i < teamIds.length; i++) {
    for (let j = i + 1; j < teamIds.length; j++) {
      pairs.push([teamIds[i], teamIds[j]]);
    }
  }
  return pairs;
}

/**
 * Berger circle-method round-robin scheduling.
 *
 * Returns an array of "rounds".  Each round is an array of [teamA, teamB] pairs
 * where no team appears more than once (bye slots are excluded).
 * N teams → N-1 rounds; every pair of teams meets exactly once.
 *
 * @param {number[]} teamIds - Array of team IDs
 * @returns {Array<Array<[number, number]>>} rounds
 */
export function bergerRounds(teamIds) {
  const n = teamIds.length;
  if (n < 2) return [];

  // Pad to even count with sentinel -1 (bye)
  const ids = n % 2 === 0 ? [...teamIds] : [...teamIds, -1];
  const half = ids.length / 2;
  const rounds = [];

  for (let r = 0; r < ids.length - 1; r++) {
    const roundPairs = [];
    for (let i = 0; i < half; i++) {
      const a = ids[i];
      const b = ids[ids.length - 1 - i];
      if (a !== -1 && b !== -1) roundPairs.push([a, b]);
    }
    rounds.push(roundPairs);

    // Rotate all except the first element (Berger rotation)
    const last = ids.pop();
    ids.splice(1, 0, last);
  }

  return rounds;
}

/**
 * Return a normalised pair key (smaller id first) for deduplication.
 */
export function pairKey(id1, id2) {
  const a = Math.min(id1, id2);
  const b = Math.max(id1, id2);
  return `${a}-${b}`;
}
